import React, { useRef, useCallback, useState } from 'react';
import {
    Bold, Italic, Underline, List, ListOrdered,
    Link, AlignLeft, AlignCenter, AlignRight, Type,
    Code, Heading1, Heading2, Quote, Minus
} from 'lucide-react';

interface RichTextEditorProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    minHeight?: number;
}

type FormatCommand = 'bold' | 'italic' | 'underline' | 'insertUnorderedList' |
    'insertOrderedList' | 'createLink' | 'justifyLeft' | 'justifyCenter' |
    'justifyRight' | 'formatBlock' | 'insertHorizontalRule' | 'removeFormat';

interface ToolbarButtonProps {
    icon: React.ElementType;
    command: FormatCommand;
    value?: string;
    title: string;
    onClick: () => void;
    active?: boolean;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({ icon: Icon, title, onClick, active }) => (
    <button
        type="button"
        onClick={onClick}
        title={title}
        className={`p-1.5 rounded hover:bg-neutral-200 transition-colors ${active ? 'bg-neutral-200 text-neutral-900' : 'text-neutral-600'}`}
    >
        <Icon size={16} />
    </button>
);

const ToolbarDivider: React.FC = () => (
    <div className="w-px h-5 bg-neutral-200 mx-1" />
);

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
    value,
    onChange,
    placeholder = 'Start typing...',
    minHeight = 300
}) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [linkUrl, setLinkUrl] = useState('');
    const savedSelection = useRef<Range | null>(null);

    const execCommand = useCallback((command: FormatCommand, value?: string) => {
        document.execCommand(command, false, value);
        editorRef.current?.focus();
        // Trigger change after command
        if (editorRef.current) {
            onChange(editorRef.current.innerHTML);
        }
    }, [onChange]);

    const handleFormat = useCallback((command: FormatCommand, value?: string) => {
        execCommand(command, value);
    }, [execCommand]);

    const handleHeading = useCallback((tag: string) => {
        execCommand('formatBlock', tag);
    }, [execCommand]);

    const handleLink = useCallback(() => {
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
            savedSelection.current = selection.getRangeAt(0).cloneRange();
        }
        setShowLinkModal(true);
    }, []);

    const insertLink = useCallback(() => {
        if (savedSelection.current && linkUrl) {
            const selection = window.getSelection();
            selection?.removeAllRanges();
            selection?.addRange(savedSelection.current);
            execCommand('createLink', linkUrl);
        }
        setShowLinkModal(false);
        setLinkUrl('');
        savedSelection.current = null;
    }, [linkUrl, execCommand]);

    const handleInput = useCallback(() => {
        if (editorRef.current) {
            onChange(editorRef.current.innerHTML);
        }
    }, [onChange]);

    const handlePaste = useCallback((e: React.ClipboardEvent) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text/html') || e.clipboardData.getData('text/plain');
        document.execCommand('insertHTML', false, text);
        handleInput();
    }, [handleInput]);

    // Initialize with value
    React.useEffect(() => {
        if (editorRef.current && editorRef.current.innerHTML !== value) {
            editorRef.current.innerHTML = value;
        }
    }, [value]);

    return (
        <div className="border border-neutral-200 rounded-lg overflow-hidden bg-white">
            {/* Toolbar */}
            <div className="flex items-center gap-0.5 p-2 border-b border-neutral-200 bg-neutral-50 flex-wrap">
                <ToolbarButton
                    icon={Bold}
                    command="bold"
                    title="Bold (Ctrl+B)"
                    onClick={() => handleFormat('bold')}
                />
                <ToolbarButton
                    icon={Italic}
                    command="italic"
                    title="Italic (Ctrl+I)"
                    onClick={() => handleFormat('italic')}
                />
                <ToolbarButton
                    icon={Underline}
                    command="underline"
                    title="Underline (Ctrl+U)"
                    onClick={() => handleFormat('underline')}
                />

                <ToolbarDivider />

                <ToolbarButton
                    icon={Heading1}
                    command="formatBlock"
                    title="Heading 1"
                    onClick={() => handleHeading('h1')}
                />
                <ToolbarButton
                    icon={Heading2}
                    command="formatBlock"
                    title="Heading 2"
                    onClick={() => handleHeading('h2')}
                />
                <ToolbarButton
                    icon={Type}
                    command="formatBlock"
                    title="Paragraph"
                    onClick={() => handleHeading('p')}
                />

                <ToolbarDivider />

                <ToolbarButton
                    icon={List}
                    command="insertUnorderedList"
                    title="Bullet List"
                    onClick={() => handleFormat('insertUnorderedList')}
                />
                <ToolbarButton
                    icon={ListOrdered}
                    command="insertOrderedList"
                    title="Numbered List"
                    onClick={() => handleFormat('insertOrderedList')}
                />
                <ToolbarButton
                    icon={Quote}
                    command="formatBlock"
                    title="Quote"
                    onClick={() => handleHeading('blockquote')}
                />

                <ToolbarDivider />

                <ToolbarButton
                    icon={AlignLeft}
                    command="justifyLeft"
                    title="Align Left"
                    onClick={() => handleFormat('justifyLeft')}
                />
                <ToolbarButton
                    icon={AlignCenter}
                    command="justifyCenter"
                    title="Align Center"
                    onClick={() => handleFormat('justifyCenter')}
                />
                <ToolbarButton
                    icon={AlignRight}
                    command="justifyRight"
                    title="Align Right"
                    onClick={() => handleFormat('justifyRight')}
                />

                <ToolbarDivider />

                <ToolbarButton
                    icon={Link}
                    command="createLink"
                    title="Insert Link"
                    onClick={handleLink}
                />
                <ToolbarButton
                    icon={Minus}
                    command="insertHorizontalRule"
                    title="Horizontal Rule"
                    onClick={() => handleFormat('insertHorizontalRule')}
                />
                <ToolbarButton
                    icon={Code}
                    command="removeFormat"
                    title="Clear Formatting"
                    onClick={() => handleFormat('removeFormat')}
                />
            </div>

            {/* Editor Area */}
            <div
                ref={editorRef}
                contentEditable
                onInput={handleInput}
                onPaste={handlePaste}
                data-placeholder={placeholder}
                className="p-4 outline-none prose prose-sm max-w-none focus:ring-2 focus:ring-blue-500/20"
                style={{
                    minHeight: `${minHeight}px`,
                    maxHeight: '600px',
                    overflowY: 'auto'
                }}
            />

            {/* Link Modal */}
            {showLinkModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl p-4 w-80">
                        <h3 className="text-sm font-semibold mb-3">Insert Link</h3>
                        <input
                            type="url"
                            value={linkUrl}
                            onChange={(e) => setLinkUrl(e.target.value)}
                            placeholder="https://example.com"
                            className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm mb-3"
                            autoFocus
                        />
                        <div className="flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowLinkModal(false);
                                    setLinkUrl('');
                                }}
                                className="px-3 py-1.5 text-sm text-neutral-600 hover:bg-neutral-100 rounded-lg"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={insertLink}
                                disabled={!linkUrl}
                                className="px-3 py-1.5 text-sm bg-neutral-900 text-white rounded-lg hover:bg-neutral-800 disabled:opacity-50"
                            >
                                Insert
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Editor Styles */}
            <style>{`
                [contenteditable]:empty:before {
                    content: attr(data-placeholder);
                    color: #9ca3af;
                    pointer-events: none;
                }
                [contenteditable] h1 { font-size: 1.5rem; font-weight: 700; margin: 0.5rem 0; }
                [contenteditable] h2 { font-size: 1.25rem; font-weight: 600; margin: 0.5rem 0; }
                [contenteditable] p { margin: 0.5rem 0; }
                [contenteditable] ul, [contenteditable] ol { margin: 0.5rem 0; padding-left: 1.5rem; }
                [contenteditable] blockquote { 
                    border-left: 3px solid #e5e7eb; 
                    padding-left: 1rem; 
                    margin: 0.5rem 0;
                    color: #6b7280;
                }
                [contenteditable] a { color: #2563eb; text-decoration: underline; }
                [contenteditable] hr { margin: 1rem 0; border: 0; border-top: 1px solid #e5e7eb; }
            `}</style>
        </div>
    );
};

export default RichTextEditor;
